import React, { useState, useMemo, ChangeEvent } from "react";
import { FiSearch, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { AiOutlineSearch } from "react-icons/ai";
import { MdGroups } from "react-icons/md";
import { IoShieldCheckmarkOutline } from "react-icons/io5";
import { LuLayoutGrid, LuList } from "react-icons/lu";
import AnimateHeight from "react-animate-height";
import classNames from "classnames";
import { useCockpitPreferences } from "@/hooks/play/useCockpitPreferences";
import { Guild, NftOwnership } from "@devour/client";
import GuildCard from "./GuildCard";
import GuildsExploreListViewItem from "./GuildsExploreListViewItem";
import GoFrensNftCard from "@/components/goFrens/GoFrensNftCard";
import FrameOneSwitchInput from "@/components/inputs/FrameOneSwitchInput";
import GoFrensNoResultsGraphic from "@/components/goFrens/GoFrensNoResultsgraphic";
import "./GuildSearchCockpit.scss";

enum ViewStates {
    GRID,
    LIST,
}

interface GuildSearchCockpitProps {
    guilds: Guild[];
    badges: NftOwnership[];
    className?: string;
}

export default function GuildSearchCockpit({
    guilds,
    badges,
    className,
}: GuildSearchCockpitProps) {
    const [searchValue, setSearchValue] = useState("");
    const [viewState, setViewState] = useState<ViewStates>(ViewStates.GRID);
    const { isExpanded, toggleCard } = useCockpitPreferences();
    const expanded = isExpanded("guild-discovery", false); // Default to collapsed

    // Sort guilds by rank (ranked guilds first)
    const sortedGuilds = useMemo(() => {
        return [...guilds].sort((a, b) => {
            if (!a.monthlyRank && !b.monthlyRank) return 0;
            if (!a.monthlyRank) return 1;
            if (!b.monthlyRank) return -1;
            return a.monthlyRank - b.monthlyRank;
        });
    }, [guilds]);

    // Filter guilds based on search - live filtering
    const queriedGuilds = useMemo(
        () => sortedGuilds.filter(guild =>
            guild.name?.toLowerCase().includes(searchValue.toLowerCase())),
        [sortedGuilds, searchValue],
    );

    // Don't show badges in guild search - remove badge filtering entirely
    const queriedBadges = useMemo(
        () => [], // Always empty - we don't want badges in guild search
        [],
    );

    const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
    };

    const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        if (e.type === "click" || (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === " ") {
            toggleCard("guild-discovery");
        }
    };

    return (
        <div className={classNames("guild-search-cockpit", className, { expanded, collapsed: !expanded })}>
            {/* Search Header */}
            <div
                className="guild-search-cockpit__header"
                onClick={handleToggle}
                onKeyDown={handleToggle}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-controls="guild-discovery-content"
            >
                <div className="header-left">
                    <MdGroups className="icon" />
                    <h4>Guild Discovery</h4>
                </div>
                <button
                    className="guild-search-cockpit__collapse-toggle"
                    aria-label={expanded ? "Collapse" : "Expand"}
                >
                    {expanded ? <FiChevronUp /> : <FiChevronDown />}
                </button>
            </div>

            <AnimateHeight
                id="guild-discovery-content"
                duration={300}
                height={expanded ? "auto" : 0}
            >
                <div className="guild-search-cockpit__content-wrapper">
                    {/* Search and Toggle Row */}
                    <div className="guild-search-cockpit__search-row">
                        {/* Search Input - Live filtering, no submit button */}
                        <div className="guild-search-cockpit__search">
                            <div className="search-container">
                                <AiOutlineSearch className="search-icon" />
                                <input
                                    value={searchValue}
                                    onChange={onSearchChange}
                                    type="search"
                                    placeholder="Search guilds or badges..."
                                    className="search-input"
                                />
                            </div>
                        </div>

                        {/* View Toggle */}
                        <FrameOneSwitchInput
                            name="layout-change"
                            onToggle={(val) => setViewState(val as ViewStates)}
                            value={viewState}
                            options={[
                                { value: ViewStates.GRID, render: <LuLayoutGrid /> },
                                { value: ViewStates.LIST, render: <LuList /> },
                            ]}
                        />
                    </div>

                    {/* Content Area */}
                    <div className="guild-search-cockpit__content">
                        {/* Show no results if searching and nothing found */}
                        {searchValue && queriedGuilds.length === 0 && queriedBadges.length === 0
                            ? <GoFrensNoResultsGraphic message="Sorry, we couldn't find anything." />
                            : <>
                                {/* Guilds Section */}
                                {queriedGuilds.length > 0 &&
              <div className="content-section">
                  <div className="section-header">
                      <MdGroups className="section-icon" />
                      <h4>{searchValue ? "Search Results" : "All Guilds"}</h4>
                      <span className="count">{queriedGuilds.length}</span>
                  </div>

                  {viewState === ViewStates.GRID
                      ? <div className="guilds-grid">
                          {queriedGuilds.map(guild =>
                              <GuildCard key={guild.id} guild={guild} />)}
                      </div>
                      : <div className="guilds-list">
                          <div className="list-header">
                              <div className="list-header-item">Guild</div>
                              <div className="list-header-item right-align">Members</div>
                              <div className="list-header-item right-align">Rank</div>
                          </div>
                          <div className="list-body">
                              {queriedGuilds.map(guild =>
                                  <GuildsExploreListViewItem
                                      key={guild.id}
                                      guild={guild}
                                      games={[]}
                                  />)}
                          </div>
                      </div>
                  }
              </div>
                                }

                                {/* Badges Section - Only show when searching */}
                                {searchValue && queriedBadges.length > 0 &&
              <div className="content-section">
                  <div className="section-header">
                      <IoShieldCheckmarkOutline className="section-icon" />
                      <h4>POP Badges</h4>
                      <span className="count">{queriedBadges.length}</span>
                  </div>
                  <div className="badges-grid">
                      {queriedBadges.map(badge =>
                          <GoFrensNftCard
                              key={badge.id}
                              ownership={badge}
                              className="search-badge-card"
                          />)}
                  </div>
              </div>
                                }
                            </>
                        }
                    </div>
                </div>
            </AnimateHeight>
        </div>
    );
}